import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { employeeRequests } from "api/employeeApi";
import { getUserData } from "server/security/utils";
import { EmployeePositionTypes } from "utils/constants";

const hasPositionType = (positionTypes, nameEn) =>
  positionTypes.some(type => type?.nameEn?.trim().toLowerCase() === nameEn.toLowerCase());

/**
 * A substitute is mandatory only for employees whose position type is "Администрация".
 * Employees holding positions of both types count as "Преподаватели" — not required.
 *
 * @param {number|string} [employeeId] the employee picked in the "Служител" field;
 *   when omitted, the logged-in user is inspected instead.
 * @returns {{ isRequired: boolean, isLoading: boolean }} `isRequired` is only meaningful
 *   once `isLoading` is false — until then it defaults to false, so callers must not treat
 *   the form as submittable while loading.
 */
export const useIsSubstituteRequired = employeeId => {
  const dataProvider = useDataProvider();
  const targetEmployeeId = employeeId ?? getUserData()?.employeeId ?? null;
  const [isRequired, setIsRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(targetEmployeeId));

  useEffect(() => {
    if (!targetEmployeeId) {
      setIsRequired(false);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    dataProvider
      .customRequest(employeeRequests.getEmployeePositionTypes(targetEmployeeId), null, "GET")
      .then(({ data }) => {
        if (cancelled) return;
        const positionTypes = Array.isArray(data) ? data : [];
        setIsRequired(
          hasPositionType(positionTypes, EmployeePositionTypes.Administration) &&
          !hasPositionType(positionTypes, EmployeePositionTypes.Professors),
        );
      })
      .catch(err => {
        if (cancelled) return;
        // Non-blocking: fall back to an optional field, the backend still validates.
        console.error(err);
        setIsRequired(false);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetEmployeeId, dataProvider]);

  return { isRequired, isLoading };
};
